import type { State } from '../State';
import { PetSpeciesID, PetState } from '../pet';
import * as Pet from '../pet';
import createAsyncGetters from '../config/asyncGetters/createAsyncGetters';
import { getVisiblePetState } from '../fate/fate.getters';
import {
  getPlayerProfile,
  getPlayerProfileAvatarFrame,
} from '../profile/profile.getters';
import type { AvatarFrameId } from '../profile/frames';
import {
  SharedStatesAsyncGetterAPI,
  getSharedStateAsyncGettersAPI,
} from '../config/asyncGetters/createSharedStateAsyncGetters';
import {
  getAttributeValues,
  getCurrentEvolution,
  getPetAge,
  getPetName,
} from '../pet/pet.getters';
import { canAddFriends, isBuruFriend } from '../friends/friends.getters';
import { canVisit } from '../visit/visit.getters';
import { isOnlineForVisit } from '../visit/visit.presence';
import type { Presence } from '@play-co/replicant';
import * as History from '../history';

/**
 * Full player profile info
 */
type FullPlayerProfile = {
  // Player info
  playerId: string;
  playerName: string;
  playerAvatar: string;
  playerAvatarFrame: AvatarFrameId;
  playerLevel: number;
  playerJoinedAt: number;
  playerTeamName?: string;
  playerTeamAvatar?: string;

  // Pet info
  petName: string;
  petSpecies: PetSpeciesID;
  petState: PetState;
  petAge: number;
  petStomachLevel: number;
  petLoveLevel: number;
  petsRaised: number;
  petHighestAge: number;
  petDuos: number;

  // Some useful flags
  isMe: boolean;
  isFriend: boolean;
  isOnline: boolean;
  canVisit: boolean;
  canAddFriend: boolean;
};

export const profileAsyncGetters = createAsyncGetters({
  /**
   * Retrieve full player profile information, including pet and team info
   * @param playerId The player ID to retrieve profile for - can be own ID
   * @param api
   * @returns
   */
  getFullPlayerProfile: async (playerId: string, api) => {
    const now = api.date.now();
    const ownState = await api.getOwnState();

    // The other player as their fetched record (`{ state, presence }`), so `canVisit` and
    // `isOnline` can be answered from their presence.
    let player: { state: State; presence?: Presence };

    if (ownState.id === playerId) {
      player = { state: ownState };
    } else {
      const entry = (await api.fetchStates([playerId]))?.[playerId];
      if (!entry?.state) return undefined;
      player = entry;
    }

    const playerState = player.state;

    // Get player profile
    const profile = getPlayerProfile(
      playerState,
      playerState.publicProfile?.name,
      playerState.publicProfile?.photo,
      undefined,
      undefined,
      ownState.id === playerState.id,
    );

    // Get team info if any
    const teamProfile = await getTeamProfile(
      playerState,
      getSharedStateAsyncGettersAPI(api),
    );

    // Get pet attributes info
    const attributes = getPetAttributes(playerState, now);
    const history = getHistoryProps(playerState, now);

    // Flags
    const isMe = ownState.id === playerState.id;
    const isFriend = isBuruFriend(ownState, playerId);
    const canAddFriend = !isMe && !isFriend && canAddFriends(ownState);

    return {
      // Player info
      playerId: playerState.id,
      playerName: profile.name,
      playerAvatar: profile.photo,
      playerAvatarFrame: getPlayerProfileAvatarFrame(playerState),
      playerLevel: playerState.match2.level,
      playerJoinedAt: playerState.createdAt,
      playerTeamName: teamProfile?.name,
      playerTeamAvatar: teamProfile?.avatar,

      // Pet info
      petName: getPetName(playerState),
      petSpecies: getCurrentEvolution(playerState)?.species ?? 'ball',
      petState: getVisiblePetState(playerState, now, true),
      petAge: getPetAge(playerState, now),
      petStomachLevel: attributes.petStomachLevel,
      petLoveLevel: attributes.petLoveLevel,
      petsRaised: history.petsRaised,
      petHighestAge: history.petHighestAge,
      petDuos: history.petDuos,

      // Some useful flags
      isMe,
      isOnline: isOnlineForVisit(player.presence),
      canVisit: canVisit(ownState, player, now),
      isFriend,
      canAddFriend,
    } satisfies FullPlayerProfile;
  },
});

/**
 * Helper to retrieve team profile information
 */
async function getTeamProfile(state: State, api: SharedStatesAsyncGetterAPI) {
  if (!state.team) return undefined;

  const teamId = state.team.currentTeamId;
  if (!teamId) return undefined;

  // Fetch team state
  const teamState = await api.sharedStates.team.fetch(teamId);
  if (!teamState) return undefined;

  // Extract profile info
  const teamProfile = teamState.global.profile;
  return {
    name: teamProfile?.name,
    avatar: teamProfile?.icon,
  };
}

/**
 * Helper to retrieve pet attribute levels
 */
function getPetAttributes(state: State, now: number) {
  const petAttributes = getAttributeValues(state, now);
  // Pre-compute stomach and love levels as ratios, ready for display
  const { max } = Pet.ruleset.attributeBounds;
  const petStomachLevel = petAttributes.stomach / max;
  const petLoveLevel = petAttributes.love / max;

  // Return results
  return { petStomachLevel, petLoveLevel };
}

/**
 * Helper to retrieve history-based properties
 */
function getHistoryProps(state: State, now: number) {
  // Retrieve user's pet history
  const history = History.getters.getHistory(state);

  // Find the highest age ever reached by the pet in history
  const pastHighestAge = history.reduce((maxAge, record) => {
    return Math.max(maxAge, record.age);
  }, 0);

  // Consider current pet age as well
  const currentPetAge = getPetAge(state, now);
  const petHighestAge = Math.max(pastHighestAge, currentPetAge);

  // Count number of pet duos in history
  const petDuos = history.filter((record) => record.type === 'duo').length;

  // Calculate number of pets raised. Count +1 for current pet.
  const petsRaised = state.petRaised + 1;

  // Return results
  return {
    petHighestAge,
    petDuos,
    petsRaised,
  };
}
